import { useMemo, useRef, type ClipboardEvent, type KeyboardEvent } from "react";

type Props = {
  value: string;
  onChange: (digits: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  boxClassName?: string;
  validColor?: string;
  invalidColor?: string;
};

const DEFAULT_BOX_CLASS = "h-14 w-14 text-center rounded-[10px] border-2 bg-white px-1 text-base text-[#0B1F16] outline-none focus:ring-2 focus:ring-[#00553E]/30 disabled:opacity-60";

/** Saisie d'un identifiant à 10 chiffres en 5 groupes de 2, avec navigation clavier et collage — cf. QuickDiagnostic. */
export default function NipDigitInput({
  value,
  onChange,
  disabled,
  invalid,
  boxClassName,
  validColor = "#D1E2DD",
  invalidColor = "#F87171",
}: Props) {
  const partRefs = useRef<Array<HTMLInputElement | null>>([]);
  const parts = useMemo(
    () => [value.slice(0, 2), value.slice(2, 4), value.slice(4, 6), value.slice(6, 8), value.slice(8, 10)],
    [value]
  );

  const updatePart = (index: number, rawValue: string) => {
    const digits = rawValue.replace(/\D/g, "").slice(0, 2);
    const next = [...parts];
    next[index] = digits;
    onChange(next.join("").slice(0, 10));
    if (digits.length === 2 && index < 4) {
      requestAnimationFrame(() => partRefs.current[index + 1]?.focus());
    }
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (event.key === "Backspace" && input.value.length === 0 && index > 0) {
      requestAnimationFrame(() => {
        const prev = partRefs.current[index - 1];
        if (!prev) return;
        prev.focus();
        try {
          prev.setSelectionRange(prev.value.length, prev.value.length);
        } catch {
          /* ignore */
        }
      });
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 10);
    if (!pasted) return;
    event.preventDefault();
    onChange(pasted);
    const nextIndex = Math.min(4, Math.floor((pasted.length - 1) / 2));
    requestAnimationFrame(() => partRefs.current[nextIndex]?.focus());
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {parts.map((part, index) => (
        <input
          key={`nip-part-${index}`}
          ref={(el) => {
            partRefs.current[index] = el;
          }}
          inputMode="numeric"
          autoComplete="off"
          maxLength={2}
          value={part}
          onChange={(e) => updatePart(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          aria-invalid={invalid}
          className={boxClassName ?? DEFAULT_BOX_CLASS}
          style={{ borderColor: invalid ? invalidColor : validColor }}
        />
      ))}
    </div>
  );
}
